import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { catchError, firstValueFrom, of } from 'rxjs';
import { GlobalService } from 'src/app/_services/global.service';
import { PermissionCheckerService } from 'src/app/_services/permission-checker.service';
import { UserService } from 'src/app/_services/user.service';

@Component({
  selector: 'app-business',
  templateUrl: './business.component.html',
  styleUrls: ['./business.component.scss']
})
export class BusinessComponent implements OnInit {
  //   { label: 'Loyalty Program Configuration' },
  items: MenuItem[] = []
  private readonly _globals = GlobalService
  isTabsLoading = true
  tabSkeletonWidths: string[] = ['9rem', '11rem', '13rem', '12rem', '10rem', '12rem', '11rem']
  cameFromBusinessSetup = false

  activeItem: any = { label: 'General' }
  constructor(
    public _permissionsCheckerService: PermissionCheckerService,
    private readonly _route: ActivatedRoute,
    private readonly _router: Router,
    private readonly _userService: UserService,
  ) { }

  ngOnInit(): void {
    this.cameFromBusinessSetup = this._route.snapshot.queryParamMap.get('from') === 'business-setup';
    void this.initializeTabs();
  }

  goBackToBusinessSetup(): void {
    this._router.navigate(['/app/business-setup']);
  }

  private async initializeTabs(): Promise<void> {
    this.isTabsLoading = true
    try {
      await this.refreshUserPermissions();
      await this.buildTabs();
      this.ensureActiveItemIsVisible();
      this.goToRoute()
    } finally {
      this.isTabsLoading = false
    }
  }

  private async refreshUserPermissions(): Promise<void> {
    this._userService.setCurrentUserFromLocalStorage();
    const userName = String(
      this._globals.userInfo?.username
      || this._globals.userInfo?.userName
      || this._userService.currentUserObj?.userName
      || ''
    ).trim();

    if (!userName) {
      return;
    }

    const res: any = await firstValueFrom(
      this._userService.getUserByUserName(userName).pipe(catchError(() => of(null)))
    );

    if (!(res?.success && res?.data)) {
      return;
    }

    const user = res.data;
    this._globals.userInfo = user;
    const setup = user?.businessSetup?.[0];
    if (setup) {
      sessionStorage.setItem('business-Info', JSON.stringify(setup));
    }

    let rights: any[] = [];
    if (Array.isArray(user?.userRights)) {
      rights = user.userRights;
    } else if (Array.isArray(user?.UserRights)) {
      rights = user.UserRights;
    }

    this._userService.loadPermissions(rights);
  }

  private async buildTabs(): Promise<void> {
    this.items = [{
      label: 'General',
      visible: await this._permissionsCheckerService.isPermissionValid(['2027', '2028', '2029', '2030', '2031', '2032', '2034', '2035', '2061', '2103']),
    },
    {
      id: 'tax-rate',
      label: 'Tax Rate',
      visible: (!this.isProdDomain() || this.isCurrentUser('younksnutritioncentral@yahoo.com')) && await this._permissionsCheckerService.isPermissionValid(['2018']),
    },
    {
      id: 'tax-rate-v2',
      label: this.isProdDomain() ? 'Tax Rate' : 'Tax Rate (V2)',
      visible: (!this.isProdDomain() || !this.isCurrentUser('younksnutritioncentral@yahoo.com')) && await this._permissionsCheckerService.isPermissionValid(['2018']),
    },
    {
      label: 'Tender Configuration',
      visible: await this._permissionsCheckerService.isPermissionValid(['2024']),

    },
    {
      label: 'Shipping Configuration',
      visible: await this._permissionsCheckerService.isPermissionValid(['2094']),

    },
    {
      label: 'Shipping Configuration (V2)',
      visible: !this.isShippingConfigV2Hidden() && await this._permissionsCheckerService.isPermissionValid(['2094']),

    },
    {
      label: 'Payment Processing',
      visible: await this._permissionsCheckerService.isPermissionValid(['2094']),

    },
    {
      label: 'Purchase Order',
      visible: await this._permissionsCheckerService.isPermissionValid(['2093']),
    },
      // { label: 'Customer Group Configuration',
      // // visible: await this._permissionsCheckerService.isPermissionValid(['2094']),
      // },
    ]
  }

  private ensureActiveItemIsVisible(): void {
    const activeLabel = this.activeItem?.label;
    const matchedActiveItem = this.items.find((item) => item.label === activeLabel && item.visible !== false);
    if (matchedActiveItem) {
      this.activeItem = matchedActiveItem;
      return;
    }

    const firstVisibleItem = this.items.find((item) => item.visible !== false);
    if (firstVisibleItem) {
      this.activeItem = firstVisibleItem;
    }
  }

  goToRoute() {
    let page = null;

    this._route.queryParams.subscribe(params => {
      page = params['page'];
    });
    if (page !== undefined) {
      if (page.includes('shipping')) {
        this.setActiveItemByLabel('Shipping Configuration');
      } else if (page.includes('payment-processing')) {
        this.setActiveItemByLabel('Payment Processing');
      } else if (page.includes('Taxconfig')) {
        this.setActiveItemByLabel('Tax Rate');
      }
    }
  }

  /** p-tabMenu highlights the active tab by comparing [activeItem] against entries in [model]
   *  (this.items) by object reference — assigning a freshly-built {label: ...} object (as
   *  goToRoute() used to) matches no item in that array, so the content panel below (which
   *  checks activeItem.label by value) rendered correctly while the tab strip itself never
   *  highlighted anything. Look up the real item instead, same as ensureActiveItemIsVisible(). */
  private setActiveItemByLabel(label: string): void {
    const matchedItem = this.items.find((item) => item.label === label && item.visible !== false);
    if (matchedItem) {
      this.activeItem = matchedItem;
    }
  }

  onTabItemClick(tab: any) {
    this.activeItem = tab.activeItem
  }

  // TODO re-enable: Shipping Configuration V2 is hidden on preapp.lincsell.com, app.lincsell.com, and for this specific user
  private isShippingConfigV2Hidden(): boolean {
    return window.location.host.includes('preapp.lincsell.com') || window.location.host.includes('app.lincsell.com') || this.isCurrentUser('younksnutritioncentral@yahoo.com');
  }

  private isProdDomain(): boolean {
    return window.location.host.includes('app.lincsell.com');
  }

  private isCurrentUser(email: string): boolean {
    const identity = String(
      this._globals.userInfo?.username
      || this._globals.userInfo?.userName
      || this._globals.userInfo?.email
      || ''
    ).trim().toLowerCase();
    return identity === email;
  }

}
